import React from 'react';
import { Card, Row, Col, Statistic, Typography, Space, Button } from 'antd';
import { 
  UserOutlined, 
  BankOutlined, 
  CheckCircleOutlined, 
  ClockCircleOutlined 
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';

const { Title } = Typography;

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const { t } = useTranslation();

  const quickActions = [
    ...(hasRole(['SUPERADMIN', 'ADMIN']) ? [
      {
        title: t('dashboard.createUserAction'),
        description: t('dashboard.createUserDesc'),
        action: () => navigate('/users/create'),
        type: 'primary' as const,
      }
    ] : []),
    {
      title: t('dashboard.viewCampaignsAction'),
      description: t('dashboard.viewCampaignsDesc'),
      action: () => navigate('/campaigns'),
      type: 'default' as const,
    },
  ];

  return (
    <div>
      <Title level={2}>
        {t('dashboard.welcome')}, {user?.firstName} {user?.lastName}!
      </Title>
      
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('dashboard.totalUsers')}
              value={152}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('dashboard.activeCampaigns')}
              value={28}
              prefix={<BankOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('dashboard.completedTasks')}
              value={342}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('dashboard.pending')}
              value={12}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title={t('dashboard.recentEvents')} extra={<Button type="link">{t('dashboard.viewAll')}</Button>}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <strong>{t('dashboard.userCreated', { name: 'John Doe' })}</strong>
                <div style={{ color: '#8c8c8c', fontSize: '12px' }}>{t('dashboard.hoursAgo', { count: 2 })}</div>
              </div>
              <div>
                <strong>{t('dashboard.campaignActivated', { name: 'Spring Sale' })}</strong>
                <div style={{ color: '#8c8c8c', fontSize: '12px' }}>{t('dashboard.hoursAgo', { count: 4 })}</div>
              </div>
              <div>
                <strong>{t('dashboard.systemUpdated')}</strong>
                <div style={{ color: '#8c8c8c', fontSize: '12px' }}>{t('dashboard.dayAgo', { count: 1 })}</div>
              </div>
            </Space>
          </Card>
        </Col>
        
        <Col xs={24} lg={8}>
          <Card title={t('dashboard.quickActions')}>
            <Space direction="vertical" style={{ width: '100%' }}>
              {quickActions.map((action, index) => (
                <Card.Grid 
                  key={index} 
                  style={{ width: '100%', textAlign: 'left' }}
                  onClick={action.action}
                >
                  <div>
                    <strong>{action.title}</strong>
                    <div style={{ color: '#8c8c8c', fontSize: '12px', marginTop: 4 }}>
                      {action.description}
                    </div>
                  </div>
                </Card.Grid>
              ))}
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
